import { syncOrthoFrustum } from "./viewerUtils.js";

const HISTORY_MAX = 20;

// Accepts meshObjects (shared array, mutated externally) instead of fixed modelCenter/modelSize.
export function createCameraManager({ THREE, perspCamera, orthoCamera, controls, container, meshObjects }) {

  let activeCamera = perspCamera;
  const camHistory = [];

  function computeBounds() {
    if (meshObjects.length === 0) return { center: new THREE.Vector3(), size: 100 };
    const box = new THREE.Box3();
    meshObjects.forEach((m) => box.expandByObject(m));
    const center = box.getCenter(new THREE.Vector3());
    const size   = box.getSize(new THREE.Vector3());
    return { center, size: Math.max(size.x, size.y, size.z) || 1 };
  }

  function pushCamState() {
    camHistory.push({ pos: activeCamera.position.clone(), target: controls.target.clone() });
    if (camHistory.length > HISTORY_MAX) camHistory.shift();
  }

  pushCamState();
  controls.addEventListener("end", pushCamState);

  return {
    getActiveCamera: () => activeCamera,

    pushCamState,

    fitToView() {
      if (meshObjects.length === 0) return;
      const { center, size } = computeBounds();
      pushCamState();
      const pos = new THREE.Vector3(
        center.x + size * 0.8,
        center.y + size * 0.5,
        center.z + size * 0.8,
      );
      perspCamera.position.copy(pos);
      orthoCamera.position.copy(pos);
      controls.target.copy(center);
      controls.update();
      syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
    },

    zoomBack() {
      if (camHistory.length < 2) return;
      camHistory.pop();
      const prev = camHistory[camHistory.length - 1];
      if (!prev) return;
      activeCamera.position.copy(prev.pos);
      controls.target.copy(prev.target);
      controls.update();
      syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
    },

    setView(view) {
      if (meshObjects.length === 0) return;
      const { center, size } = computeBounds();
      pushCamState();
      const d = size * 1.2;
      let pos;
      if (view === "top") {
        // Bird's eye: camera above (+Z), Y-north points up on screen
        pos = new THREE.Vector3(center.x, center.y, center.z + d * 1.5);
        perspCamera.up.set(0, 1, 0); orthoCamera.up.set(0, 1, 0);
      } else if (view === "front") {
        // Front elevation: camera south (-Y), looking north; Z is vertical on screen
        pos = new THREE.Vector3(center.x, center.y - d * 1.5, center.z);
        perspCamera.up.set(0, 0, 1); orthoCamera.up.set(0, 0, 1);
      } else {
        // Side elevation: camera east (+X), looking west; Z is vertical on screen
        pos = new THREE.Vector3(center.x + d * 1.5, center.y, center.z);
        perspCamera.up.set(0, 0, 1); orthoCamera.up.set(0, 0, 1);
      }
      perspCamera.position.copy(pos);
      orthoCamera.position.copy(pos);
      controls.target.copy(center);
      controls.update();
      syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
    },

    setCameraMode(mode) {
      if (mode === "ortho") {
        orthoCamera.position.copy(perspCamera.position);
        orthoCamera.quaternion.copy(perspCamera.quaternion);
        orthoCamera.up.copy(perspCamera.up);
        syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
        activeCamera = orthoCamera;
      } else {
        perspCamera.position.copy(orthoCamera.position);
        perspCamera.quaternion.copy(orthoCamera.quaternion);
        perspCamera.up.copy(orthoCamera.up);
        activeCamera = perspCamera;
      }
      controls.object = activeCamera;
      controls.update();
    },

    fitToBoundary(pts) {
      if (!pts || pts.length < 2) return;
      const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
      const span = Math.max(maxX - minX, maxY - minY) * 1.15;
      const dist = (span / 2) / Math.tan((55 / 2) * (Math.PI / 180));
      pushCamState();
      perspCamera.up.set(0, 1, 0); orthoCamera.up.set(0, 1, 0);
      perspCamera.position.set(cx, cy, dist);
      orthoCamera.position.set(cx, cy, dist);
      controls.target.set(cx, cy, 0);
      controls.update();
      syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
    },

    dispose() {
      controls.removeEventListener("end", pushCamState);
    },
  };
}
